/**
 * Building meters: what each measures, whether it is reporting, and the file
 * a BMS that can only export fills in.
 *
 * The platform keeps these as counter channels on metering assets (backend
 * `slt.counters`, migration 0075) and names no utility itself; the utilities
 * are this airport's, so the vocabulary lives here — which asset type is an
 * electricity meter, and which register it keeps.
 */

export type Utility = "electricity" | "water" | "gas" | "chilled_water";
export type MeterStatus = "live" | "stale" | "never";
export type ReadingKind = "cumulative" | "interval";

export interface UtilityDef {
  key: Utility;
  label: string;
  assetType: string;
  measurement: string;
  unit: string;
}

export const UTILITIES: readonly UtilityDef[] = [
  {
    key: "electricity",
    label: "Electricity",
    assetType: "electricity_meter",
    measurement: "energy_register_kwh",
    unit: "kWh",
  },
  { key: "water", label: "Water", assetType: "water_meter", measurement: "water_register_m3", unit: "m³" },
  { key: "gas", label: "Gas", assetType: "gas_meter", measurement: "gas_register_m3", unit: "m³" },
  {
    key: "chilled_water",
    label: "Chilled water",
    assetType: "chilled_water_meter",
    measurement: "chilled_water_register_kwh",
    unit: "kWh(th)",
  },
];

export const METER_ASSET_TYPES = UTILITIES.map((u) => u.assetType);

export function utilityOf(assetType: string): UtilityDef | undefined {
  return UTILITIES.find((u) => u.assetType === assetType);
}

export interface MeterRow {
  assetId: string;
  assetTag: string;
  name?: string;
  assetType: string;
  assetTypeName: string;
  role?: string;
  serves?: string;
  /** Null when the meter has no feed connected. */
  channelId: string | null;
  pointKey?: string;
  readingKind?: ReadingKind;
  scale?: number;
  modulus?: number;
  staleAfterMin: number;
  rollovers: number;
  resets: number;
  measurement?: string;
  unit?: string;
  lastObservedAt?: string;
  lastValue?: number;
  lastSource?: string;
  ageMin?: number;
  status: MeterStatus;
}

export interface MeterRegister {
  meters: MeterRow[];
  /** The site's meter connector, once one exists: where a BMS or the gateway posts. */
  connector: { key: string; name: string; pushPath: string } | null;
  /** True for the sample estate: nothing here came from a platform. */
  demo?: boolean;
}

export interface NewMeterInput {
  assetTag: string;
  utility: Utility;
  pointKey?: string;
  readingKind: ReadingKind;
  registerDigits?: number;
  scale?: number;
  staleAfterMin?: number;
  /** Create the asset as well, when the tag is new. */
  createName?: string;
  role?: string;
}

export interface MeterUploadResult {
  received: number;
  accepted: number;
  duplicate: number;
  rejected: number;
  replayed: boolean;
  rejections: { line?: number | null; reason: string }[];
}

export const STATUS_LABEL: Record<MeterStatus, string> = {
  live: "live",
  stale: "stale",
  never: "never reported",
};

/** The pack's `meter_role`, in words: an incomer is what the utility bills, a sub-meter apportions it. */
export const ROLE_LABEL: Record<string, string> = {
  incomer: "billed incomer",
  sub: "sub-meter",
  check: "check meter",
  tenant: "tenant meter",
  generation: "on-site generation",
};

/** "2 of 4 meters reporting" — how many of a utility's meters are live. */
export function reporting(meters: readonly MeterRow[], assetType: string): { live: number; total: number } {
  const of = meters.filter((m) => m.assetType === assetType);
  return { live: of.filter((m) => m.status === "live").length, total: of.length };
}

/**
 * The file a BMS export is shaped into: one row per reading, the site's own
 * meters filled in so nobody has to guess a point key. Local times are the
 * site's; a time with an offset keeps it.
 */
export function csvTemplate(meters: readonly MeterRow[], now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:00`;
  const rows = meters
    .filter((m) => m.channelId)
    .map((m) => `${m.pointKey ?? m.assetTag},${stamp},,${m.readingKind ?? "cumulative"}`);
  return ["meter,timestamp,value,kind", ...(rows.length ? rows : [`METER-TAG,${stamp},,cumulative`])].join("\n") + "\n";
}
